import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Tag, MessageCircle } from "lucide-react";
import { thumb } from "../lib/img";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProposalPage() {
  const { proposalId } = useParams();
  const [params] = useSearchParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/proposals/${proposalId}`, { params: { t: params.get("t") || "", c: params.get("c") || undefined } })
      .then(r => setP(r.data)).catch(e => setError(e.response?.data?.detail || "Proposta non disponibile"));
  }, [proposalId, params]);

  if (error) return <div className="min-h-[60vh] flex items-center justify-center text-forest" data-testid="proposal-error">{error}</div>;
  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-sage">Caricamento…</div>;
  const shopUrl = `/fioristi/${p.shop.subdomain}`;
  return (
    <div className="bg-cream min-h-screen py-10 px-4" data-testid="public-proposal">
      <Seo title={`${p.title} · ${p.shop.name}`} description={p.text?.slice(0, 150)} noindex />
      <article className="max-w-2xl mx-auto bg-white rounded-3xl border border-cream-dark shadow-sm overflow-hidden">
        <header className="px-8 pt-10 pb-6 text-center border-b border-cream-dark">
          {p.shop.logo_url && <img src={p.shop.logo_url} alt={p.shop.name} className="h-16 mx-auto mb-3 object-contain" />}
          <p className="font-serif text-2xl text-forest">{p.shop.name}</p>
        </header>
        <div className="px-8 py-8">
          <p className="text-xs tracking-[0.2em] uppercase text-brand mb-2">{p.occasion_label}</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-forest leading-tight mb-6">{p.title}</h1>
          {p.greeting && <p className="text-forest/80 text-lg leading-loose" data-testid="proposal-greeting">{p.greeting}</p>}
          {(p.text || "").split("\n\n").map((para, i) => <p key={i} className="text-forest/80 text-base sm:text-lg leading-[1.9] mb-4 whitespace-pre-line">{para}</p>)}
          <div className="divide-y divide-cream-dark mt-8">
            {p.items.map(i => (
              <div key={i.product_id} className="py-5 flex gap-4" data-testid={`proposal-item-${i.product_id}`}>
                <img src={thumb(i.image, 320) || "/product-placeholder.svg"} alt={i.name} className="w-28 h-28 rounded-xl object-cover shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-serif text-xl text-forest">{i.name}</h3>
                  <p className="text-sm text-sage leading-relaxed mt-1">{i.description}</p>
                  <p className="mt-2 text-forest">da <strong>€{i.price.toFixed(2)}</strong> · <Link to={`/products/${i.product_id}`} className="text-brand hover:underline">Ordina ora →</Link></p>
                </div>
              </div>
            ))}
          </div>
          {p.discount && (
            <div className="mt-8 rounded-2xl border border-dashed border-amber-300 bg-amber-50/60 p-6 text-center" data-testid="proposal-discount">
              <p className="text-xs uppercase tracking-[0.2em] text-amber-800 flex items-center justify-center gap-1"><Tag className="h-3.5 w-3.5" />Riservato a te: {p.discount.percent}% di sconto</p>
              <p className="font-mono text-3xl text-forest font-bold tracking-widest mt-2">{p.discount.code}</p>
              <p className="text-xs text-sage mt-1">Inseriscilo nel riepilogo ordine · valido fino al {p.discount.expires_at?.slice(0, 10)}</p>
            </div>
          )}
          <div className="text-center mt-8">
            <Link to={shopUrl} className="inline-block bg-brand text-white px-8 py-4 rounded-full text-lg hover:bg-brand-dark transition-colors" data-testid="proposal-order-btn">Ordina ora →</Link>
          </div>
        </div>
        <footer className="px-8 py-6 bg-cream/60 border-t border-cream-dark text-sm text-sage">
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center">
            {p.shop.phone && <a href={`tel:${p.shop.phone}`} className="inline-flex items-center gap-1 hover:text-forest"><Phone className="h-4 w-4" />{p.shop.phone}</a>}
            {p.shop.whatsapp && <a href={`https://wa.me/${p.shop.whatsapp.replace(/\D/g, "")}`} className="inline-flex items-center gap-1 hover:text-forest"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
            {p.shop.email && <a href={`mailto:${p.shop.email}`} className="inline-flex items-center gap-1 hover:text-forest"><Mail className="h-4 w-4" />{p.shop.email}</a>}
            {(p.shop.address || p.shop.city) && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{p.shop.address} {p.shop.city}</span>}
          </div>
          {p.unsubscribe_token && <p className="text-center text-xs mt-4"><Link to={`/consenso/${p.unsubscribe_token}?azione=disiscrivi`} className="underline hover:text-forest" data-testid="proposal-unsubscribe">Non voglio più ricevere proposte</Link></p>}
        </footer>
      </article>
    </div>
  );
}
